import { PlanningsGroupEntity } from 'src/entities/PlanningsGroup.entity';
import { RotationRow, VehicleRowGroup } from 'src/pages/ConvenienceDispatches/adapter';

import { COURSE_DIFF_FILTER_LABELS, courseDiffFilterMatches, CourseDiffFilterValue } from './courseDiff';
import { hasHiddenOrderAttrs, isOrderAttrFilterActive, OrderAttrFilter } from './orderAttrFilter';
import { hasVehicleConstraintViolation } from './vehicleConstraintAlerts';
import { LoadTotals } from './vehicleLoad';

/**
 * 車両リストの絞り込み。1 つだけ選べる（サンプルの FilterMode と同じ）。
 *
 * `TruckDisplayStatusVo` を拡張しないのは、あちらが PlansContext / V3Plans / V3Timeline /
 * PlanningHistory でも使われていて、値を足すと関係の無い画面の分岐に漏れるため。
 * 4 値とも文字列が一致しているので、カード配車の保存値はそのまま読める。
 *
 * 回転はここに入れない（チェックボックスで多選択するため。ALL_ROTATION_FILTERS）。
 */
export const VEHICLE_FILTER_MODES = [
  'すべて表示',
  '選択中のみ表示する',
  '配車済み車両のみ表示',
  '未配車の車両のみ表示',
] as const;

/**
 * 回転の絞り込み値。3 は「3 回転目以降」で 4 回転目以降も含める（回転数に上限は無い）。
 *
 * 排他選択のモードから多選択へ移した。「1 回転目と 2 回転目だけ見る」ができず、
 * 既定でどれかの回転に絞られている状態にも見えていた（指摘 2026/08/18）。
 */
export const ALL_ROTATION_FILTERS = [1, 2, 3] as const;

export type RotationFilterValue = typeof ALL_ROTATION_FILTERS[number];

export const ROTATION_FILTER_LABELS: Record<RotationFilterValue, string> = {
  1: '1回転目',
  2: '2回転目',
  3: '3回転目以降',
};

/** localStorage から戻した値の検査（usePersistedJSON 用）。空配列は「全部外した」状態 */
export function isRotationFilterValues(value: unknown): value is RotationFilterValue[] {
  return Array.isArray(value)
    && value.every((v) => (ALL_ROTATION_FILTERS as readonly unknown[]).includes(v));
}

/** 回転で絞っているか。全部チェック（既定）と未指定は絞っていない */
export function hasRotationNarrowing(rotations?: readonly RotationFilterValue[]): boolean {
  if (!rotations) return false;
  return !ALL_ROTATION_FILTERS.every((v) => rotations.includes(v));
}

export type VehicleFilterMode = typeof VEHICLE_FILTER_MODES[number];

const MODE_VALUES: ReadonlySet<string> = new Set(VEHICLE_FILTER_MODES);

export function isVehicleFilterMode(value: unknown): value is VehicleFilterMode {
  return typeof value === 'string' && MODE_VALUES.has(value);
}

/** 短いラベル（フィルターボタンに出す）。「すべて表示」はボタンには出さない */
export const VEHICLE_FILTER_SHORT_LABELS: Record<VehicleFilterMode, string | null> = {
  すべて表示: null,
  選択中のみ表示する: '選択中のみ',
  配車済み車両のみ表示: '配車済のみ',
  未配車の車両のみ表示: '未配車のみ',
};

/**
 * グループ側で絞り込んでいるか。index 0 は「すべて表示する」の合成エントリなので、
 * それが入っている状態は絞り込んでいないのと同じ。
 */
export function hasGroupNarrowing(selectedGroupIndices: number[]): boolean {
  return selectedGroupIndices.length > 0 && !selectedGroupIndices.includes(0);
}

/**
 * フィルターボタンのラベル。効いているものを併記する（両方効いていることが
 * ボタンだけで分かるように）。何も効いていなければ「すべて」。
 */
export function buildVehicleFilterLabel(
  mode: VehicleFilterMode,
  selectedGroupIndices: number[],
  groupEntities: { name: string }[],
  extra: {
    rotations?: readonly RotationFilterValue[];
    constraintViolationOnly?: boolean;
    courseDiffFilter?: CourseDiffFilterValue;
  } = {},
): string {
  const modeLabel = VEHICLE_FILTER_SHORT_LABELS[mode];
  const narrowed = hasGroupNarrowing(selectedGroupIndices);
  const groupLabel = !narrowed
    ? null
    : selectedGroupIndices.length === 1
      ? groupEntities[selectedGroupIndices[0]]?.name ?? 'グループ'
      : `グループ${selectedGroupIndices.length}`;
  const { rotations, constraintViolationOnly } = extra;
  const rotationLabel = !hasRotationNarrowing(rotations) || !rotations
    ? null
    : rotations.length === 1
      ? ROTATION_FILTER_LABELS[rotations[0]]
      : `回転${rotations.length}`;
  const constraintLabel = constraintViolationOnly ? '制約違反のみ' : null;
  const courseDiffLabel = extra.courseDiffFilter && extra.courseDiffFilter !== 'all'
    ? COURSE_DIFF_FILTER_LABELS[extra.courseDiffFilter]
    : null;
  return [modeLabel, constraintLabel, rotationLabel, courseDiffLabel, groupLabel].filter(Boolean).join(' × ')
    || 'すべて';
}

const hasAssignedOperation = (group: VehicleRowGroup): boolean =>
  group.rows.some((row) => row.source.cards.some((card) => card.operations.length > 0));

/** 回転番号を絞り込み値に寄せる。4 回転目以降は「3 回転目以降」に入れる */
const toRotationFilterValue = (rotationNumber: number): number =>
  (rotationNumber >= 3 ? 3 : rotationNumber);

/**
 * 「◯回転目」バッジの番号を確定させる。行を落とす前に呼ぶ。
 *
 * バッジの番号はグループ内の位置で数えている（`buildDisplayRotationNumbers`）ので、
 * 落としてから数えると 1 回転目を外したときに 2 回転目が無印（= 1 回転目）に見える。
 * 落とさないときは呼ばない（同じ配列参照を返す最適化を壊さないため）。
 */
function withDisplayRotationNumbers(groups: VehicleRowGroup[]): VehicleRowGroup[] {
  return groups.map((g) => ({
    ...g,
    rows: g.rows.map((row, index) => ({
      ...row,
      displayRotationNumber: row.displayRotationNumber ?? index + 1,
    })),
  }));
}

/** 表示対象（配送形態・区間種別）で隠す案件を積んでいるか。カード配下の legRun で判定する */
const hasHiddenAttrOrders = (group: VehicleRowGroup, filter: OrderAttrFilter): boolean =>
  group.rows.some((row) => row.source.cards.some((card) => hasHiddenOrderAttrs(card.legRuns ?? [], filter)));

export type VehicleFilterParams = {
  mode: VehicleFilterMode;
  selectedRowIds: ReadonlySet<string>;
  /** チェックされている回転。未指定・全部チェックなら絞らない。空なら 1 台も出さない */
  rotations?: readonly RotationFilterValue[];
  /** 制約違反のある車両だけに絞るか（既定はチェックなし） */
  constraintViolationOnly?: boolean;
  /** 回転行ごとの制約違反 (key = RotationRow.rowId)。constraintViolationOnly のときだけ読む */
  constraintAlertsByRowId?: ReadonlyMap<string, readonly string[]>;
  /**
   * 回転行ごとの積載量 (key = RotationRow.rowId)。積載超過の判定に使う。
   *
   * 省略可にしない。渡し忘れると全行が 0 扱いになり、「制約違反のある車両のみ」で
   * 積載超過を静かに拾わなくなる。作るのは buildPeakLoadByRowId で、**絞り込み前**の
   * 全グループから作った Map を渡す（絞ったあとで作ると隠れている回転で積んだぶんが消える）。
   * 積載超過を見ない呼び出し側は空の Map を明示的に渡す。
   */
  peakLoadByRowId: ReadonlyMap<string, LoadTotals>;
  /**
   * 表示対象（配送形態・区間種別）。非表示にしている値の案件を 1 件でも積む車両は、
   * その車両ごと落とす。積んでいる案件を隠すだけだと、画面の車両が「空車」に見えてしまう。
   */
  orderAttrFilter?: OrderAttrFilter;
  /**
   * 変更案件フィルター（log_web_issues#353）。回転の行で絞る（決定 4）。未指定・'all' は絞らない。
   * 判定は行の source（VehicleRowVM）の courseChanges と cards[].courseDiffs
   */
  courseDiffFilter?: CourseDiffFilterValue;
};

/**
 * 車両リストの絞り込みを 1 か所に集める。/map とカード配車で同じ結果にする。
 *
 * 落とすものが無いときは同じ配列参照を返す。新しい配列を返すと memo 化した
 * 車両リストが毎回まるごと再描画される。
 */
export function filterVehicleRowGroups(
  groups: VehicleRowGroup[],
  {
    mode, selectedRowIds, rotations, constraintViolationOnly, constraintAlertsByRowId,
    peakLoadByRowId, orderAttrFilter, courseDiffFilter,
  }: VehicleFilterParams,
): VehicleRowGroup[] {
  let result = groups;

  if (mode === '配車済み車両のみ表示') result = result.filter(hasAssignedOperation);
  else if (mode === '未配車の車両のみ表示') result = result.filter((g) => !hasAssignedOperation(g));
  // 選択ゼロで全部消すと、選び直す手段ごと画面から消える
  else if (mode === '選択中のみ表示する' && selectedRowIds.size > 0) {
    result = withDisplayRotationNumbers(result)
      .map((g) => ({ ...g, rows: g.rows.filter((r) => selectedRowIds.has(r.rowId)) }))
      .filter((g) => g.rows.length > 0);
  }

  if (rotations && hasRotationNarrowing(rotations)) {
    const allowed = new Set<number>(rotations);
    result = withDisplayRotationNumbers(result)
      .map((g) => ({ ...g, rows: g.rows.filter((r) => allowed.has(toRotationFilterValue(r.rotationNumber))) }))
      .filter((g) => g.rows.length > 0);
  }

  if (courseDiffFilter && courseDiffFilter !== 'all') {
    const matches = (r: RotationRow) => courseDiffFilterMatches(r.source, courseDiffFilter);
    // 落とす行が無ければ参照を変えない（全行該当なら memo 済みのボードを描き直さない）。
    // 落とすときは回転のチェックと同じく先に「◯回転目」の番号を確定する
    if (!result.every((g) => g.rows.every(matches))) {
      result = withDisplayRotationNumbers(result)
        .map((g) => ({ ...g, rows: g.rows.filter(matches) }))
        .filter((g) => g.rows.length > 0);
    }
  }

  if (orderAttrFilter && isOrderAttrFilterActive(orderAttrFilter)) {
    result = result.filter((g) => !hasHiddenAttrOrders(g, orderAttrFilter));
  }

  // 判定はカード右上の警告バッジと同じ（積載超過＋行の制約違反）
  if (constraintViolationOnly) {
    result = result.filter((g) => hasVehicleConstraintViolation(g, constraintAlertsByRowId, peakLoadByRowId));
  }

  return result;
}

/**
 * 絞り込みに使うグループ一覧。先頭に「すべて表示する」の合成エントリを足す。
 *
 * 表示中の車両を 1 台も含まないグループを落とすのは、選んでも何も起きない選択肢を
 * 出さないため（退役車両だけのグループなど）。残らなければ空を返し、呼び出し側は
 * フィルタ UI ごと出さない。
 */
export function buildTruckGroupsWithAll(
  groups: PlanningsGroupEntity[],
  displayedTruckIds: number[],
): PlanningsGroupEntity[] {
  const displayed = new Set(displayedTruckIds);
  const options = groups.filter((g) => g.truckIds.some((id) => displayed.has(id)));
  if (options.length === 0) return [];
  return [{ id: 0, name: 'すべて表示する', truckIds: displayedTruckIds }, ...options];
}

/**
 * 選択された index 群から「表示してよい truckId 集合」を作る。
 *
 * null は「絞り込み無し」。空 Set（1 台も許可しない）と区別が要るので、
 * 呼び出し側は `=== null` で判定すること。
 */
export function resolveAllowedTruckIds(
  groupsWithAll: PlanningsGroupEntity[],
  selectedGroupIndices: number[],
): Set<number> | null {
  if (groupsWithAll.length === 0) return null;
  // 「すべて表示する」が選ばれていれば絞り込まない。選択が空のときは null にしない
  // （全部のチェックを外した状態であり、1 台も表示しないのが正しい）
  if (selectedGroupIndices.includes(0)) return null;
  const ids = new Set<number>();
  selectedGroupIndices.forEach((i) => {
    groupsWithAll[i]?.truckIds.forEach((id) => ids.add(id));
  });
  return ids;
}

/**
 * 永続化した選択が現在のグループ一覧と食い違ったときの直し方。直す必要が無ければ null。
 *
 * グループが削除されると保存済みの index が範囲外を指し、そのままでは絞り込みが
 * 効かなくなる。全部落ちたときに「すべて表示する」へ戻すのは、1 台も表示されない
 * 状態で操作不能になるのを防ぐため。
 */
export function correctGroupIndices(
  groupsWithAll: PlanningsGroupEntity[],
  selectedGroupIndices: number[],
): number[] | null {
  if (groupsWithAll.length === 0) return null;
  const valid = selectedGroupIndices.filter((i) => i >= 0 && i < groupsWithAll.length);
  if (valid.length === selectedGroupIndices.length) return null;
  return valid.length === 0 ? [0] : valid;
}

/** localStorage から読んだ選択 index の検証。壊れた値で絞り込みが効かなくなるのを防ぐ */
export function isNumberArray(parsed: unknown): parsed is number[] {
  return Array.isArray(parsed) && parsed.every((x) => typeof x === 'number');
}

